import LazyImage from 'components/Image/LazyImage';
import noImage from 'components/Image/noIMG.svg';
import QuantityInput from 'components/MarketItem/QuantityInput/QuantityInput';
import { formatVND } from 'containers/QuickOrder/utils';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { memo, useCallback, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import { AiMatchedProduct, TrackingAddToCartAddFromType, Uom } from 'types/schema';
import AccurateBadge from '../AccurateBadge';
import messages from '../messages';
import InlineSearch from './InlineSearch';
import { ResultRowProps } from './types';

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 16px;
`;

const Checkbox = styled.button<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${({ checked }) => (checked ? '#43A047' : '#D1D5DB')};
  background: ${({ checked }) => (checked ? '#43A047' : '#fff')};
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &::after {
    content: '';
    display: ${({ checked }) => (checked ? 'block' : 'none')};
    width: 4px;
    height: 8px;
    border: solid #fff;
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg) translate(-1px, -1px);
  }
`;

const ProductCol = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 310px;
  flex-shrink: 0;
`;

const Thumb = styled(({ noImage: _noImage, ...rest }) => <LazyImage {...rest} />)<{ noImage: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  object-fit: ${({ noImage: noImg }) => (noImg ? 'contain' : 'cover')};
  ${({ noImage: noImg }) => (noImg ? 'opacity: 0.3;' : '')}
  flex-shrink: 0;
  background: #f5f5f5;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #111827;
`;

const UnitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Unit = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #4b5563;
`;

const PurchasedTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 4px;
  border-radius: 4px;
  background: #f2f3f5;
  color: #303643;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

const UserInputText = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #6b7280;
`;

const ChangeBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #3c9041;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  align-self: flex-start;

  &:hover {
    opacity: 0.8;
  }
`;

const Price = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  width: 100px;
  flex-shrink: 0;
  text-align: left;
`;

const ConfidenceCol = styled.div`
  width: 106px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const QuantityCol = styled.div`
  width: 140px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UnmatchedRow = ({
  product,
  onToggle,
  onSwapProduct,
}: Pick<ResultRowProps, 'product' | 'onToggle' | 'onSwapProduct'>) => {
  const handleToggle = useCallback(() => onToggle(product.id), [product.id, onToggle]);
  const [isSearching, setIsSearching] = useState(false);
  const handleSelect = useCallback(
    (alt: AiMatchedProduct) => onSwapProduct(product.id, alt),
    [product.id, onSwapProduct],
  );

  const userInputText = translations(messages.userInput).replace('{input}', product.inputText);

  return (
    <Row>
      <Checkbox checked={false} onClick={handleToggle} aria-label="select" style={{ visibility: 'hidden' }} />
      <ProductCol style={{ paddingLeft: 64 }}>
        <Info>
          <UserInputText>{userInputText}</UserInputText>
          {isSearching ? (
            <InlineSearch onSelect={handleSelect} onClose={() => setIsSearching(false)} />
          ) : (
            <ChangeBtn type="button" onClick={() => setIsSearching(true)}>
              {translations(messages.searchManually)}
            </ChangeBtn>
          )}
        </Info>
      </ProductCol>
      <Price />
      <ConfidenceCol>
        <AccurateBadge level="none" />
      </ConfidenceCol>
      <QuantityCol />
    </Row>
  );
};

const MatchedRow = ({
  product,
  quantity,
  onToggle,
  onQuantityChange,
  onChangeProduct,
  loggedIn,
  goToSignIn,
}: Omit<ResultRowProps, 'onSwapProduct'>) => {
  const p = product.matchedProduct ?? null;
  const changeBtnRef = useRef<HTMLButtonElement>(null);
  const { uomInfo } = useProductUomInfo(p ?? ({} as AiMatchedProduct));

  const handleToggle = useCallback(() => onToggle(product.id), [product.id, onToggle]);
  const handleChangeProduct = useCallback(() => {
    if (changeBtnRef.current) onChangeProduct(product.id, changeBtnRef.current);
  }, [product.id, onChangeProduct]);
  const handleQuantity = useCallback(
    (value: number) => onQuantityChange(product.id, value),
    [product.id, onQuantityChange],
  );

  const userInputText = translations(messages.userInput).replace('{input}', product.inputText);

  if (!p) return null;

  const isKg = [Uom.Kilogram].includes(p.uom as Uom);

  return (
    <Row>
      <Checkbox checked={product.selected} onClick={handleToggle} aria-label="select" />
      <ProductCol>
        <Thumb noImage={!p.imageUrl} src={p.imageUrl || noImage} alt={p.name} />
        <Info>
          <Name>{p.name}</Name>
          <UnitRow>
            <Unit>{uomInfo}</Unit>
            {product.fromHistory && <PurchasedTag>{translations(messages.purchasedTag)}</PurchasedTag>}
          </UnitRow>
          <UserInputText>{userInputText}</UserInputText>
          <ChangeBtn ref={changeBtnRef} type="button" onClick={handleChangeProduct}>
            {translations(messages.changeProduct)}
          </ChangeBtn>
        </Info>
      </ProductCol>
      <Price>{formatVND(p.price)}</Price>
      <ConfidenceCol>
        <AccurateBadge level={product.confidence} />
      </ConfidenceCol>
      <QuantityCol>
        <QuantityInput
          loggedIn={loggedIn}
          productId={p.sku}
          min={isKg ? 0 : 1}
          max={999}
          value={quantity}
          onChange={handleQuantity}
          allowDecimal={isKg}
          orderDisabled={false}
          goToSignIn={goToSignIn}
          addFrom={TrackingAddToCartAddFromType.AllProduct}
          isShowTotalMoney={false}
        />
      </QuantityCol>
    </Row>
  );
};

const ResultRowDesktop = (props: ResultRowProps) => {
  if (!props.product.matchedProduct) {
    return <UnmatchedRow product={props.product} onToggle={props.onToggle} onSwapProduct={props.onSwapProduct} />;
  }
  return <MatchedRow {...props} />;
};

export default memo(ResultRowDesktop);
